/**
 * Safe timestamp rendering helpers for the UI.
 *
 * Every timestamp the screens render is a Unix-ms `number` from the contract (`timestamp_ms`), but a
 * value can still be absent, `NaN`, non-finite, or out of the ECMAScript Date range at runtime — a
 * truncated response, a malformed persisted custom window, a server that omits an optional field.
 * `new Date(bad).toISOString()` THROWS a `RangeError: Invalid time value` in that case, and because
 * the screens called it inline during render, one bad timestamp took down the whole screen through
 * the error boundary ("Something went wrong / Invalid time value").
 *
 * These helpers never throw. `toValidDate` is the single gate; the formatters fall back to a dash and
 * `isoAttr` returns `undefined` so a `<time dateTime>` attribute is simply omitted (valid HTML) rather
 * than carrying — or crashing on — a bad value. Note `Date#toLocaleString`/`toLocaleTimeString` do NOT
 * throw (they return the string "Invalid Date"); only `toISOString` throws, but we route every call
 * through here so the displayed text is an honest dash instead of the literal "Invalid Date".
 */

/** The text shown when a timestamp is missing or unparseable. */
export const INVALID_TIME_PLACEHOLDER = "—";

/** A valid `Date` for `ms`, or `null` when `ms` is absent, non-finite, or out of Date's range. */
export function toValidDate(ms: number | null | undefined): Date | null {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return null;
  const date = new Date(ms);
  // `getTime()` is NaN exactly when the Date is invalid (e.g. ms outside ±8.64e15).
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * An ISO-8601 string for the `dateTime` attribute of a `<time>` element, or `undefined` when the
 * value is invalid. Passing `undefined` to the attribute omits it, which is valid and never throws —
 * unlike calling `toISOString()` on an invalid Date inline.
 */
export function isoAttr(ms: number | null | undefined): string | undefined {
  const date = toValidDate(ms);
  return date === null ? undefined : date.toISOString();
}

/** A localized date-and-time string, or the placeholder when the value is invalid. */
export function formatDateTime(ms: number | null | undefined): string {
  const date = toValidDate(ms);
  return date === null ? INVALID_TIME_PLACEHOLDER : date.toLocaleString();
}

/** A localized time-of-day string, or the placeholder when the value is invalid. */
export function formatTimeOfDay(ms: number | null | undefined): string {
  const date = toValidDate(ms);
  return date === null ? INVALID_TIME_PLACEHOLDER : date.toLocaleTimeString();
}
